/**
 * Admin → Pipeline Prompts list.
 *
 * Tenant-aware (JAM-202). For Wiz the rows show editable hand-tuned
 * prompts in S3 (source: "tenant_s3"); for any tenant on the baselineKey
 * track the rows show the shared platform baselines as read-only — the
 * Edit link is disabled with a tooltip pointing the operator at the
 * repo file + PR flow.
 *
 * One row per agent for the active tenant, sourced from the agents
 * table (not the legacy hardcoded PIPELINE_AGENTS list).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { listAdminPrompts } from "../services/admin.js";
import ErrorBubble from "../components/ErrorBubble.jsx";
import LoadingSkeleton from "../components/LoadingSkeleton.jsx";

function formatBytes(bytes) {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  return `${(bytes / 1024).toFixed(1)} KB`;
}

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

// Backend agentId is `agent-N`; the existing edit route takes the
// numeric form. Strip the prefix; fall through to the raw value if the
// shape ever changes.
function agentIdToRouteSegment(agentId) {
  if (typeof agentId !== "string") return agentId;
  return agentId.startsWith("agent-") ? agentId.slice(6) : agentId;
}

function SourceBadge({ source }) {
  if (source === "tenant_s3") {
    return <span className="admin-table__badge admin-table__badge--neutral">Tenant S3</span>;
  }
  if (source === "platform_baseline") {
    return <span className="admin-table__badge admin-table__badge--info">Platform baseline</span>;
  }
  return <span className="admin-table__badge admin-table__badge--warn">Unknown</span>;
}

export default function AdminPrompts() {
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    listAdminPrompts()
      .then((data) => {
        if (cancelled) return;
        setPrompts(data.prompts || []);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="admin-page">
      <div className="admin-page__header">
        <h1>Pipeline Prompts</h1>
      </div>
      {error && <ErrorBubble message={error} />}
      {loading ? (
        <LoadingSkeleton variant="line" />
      ) : (
        <>
          <p className="admin-page__hint">
            Wiz tenants edit prompts in-place (Tenant S3). Other tenants run on
            shared platform baselines — read-only here; edit{" "}
            <code>pipeline/prompts/platform/&lt;file&gt;.md</code> in the repo
            and open a PR for those.
          </p>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Agent</th>
                <th>Source</th>
                <th>Filename</th>
                <th>Size</th>
                <th>Last Modified</th>
              </tr>
            </thead>
            <tbody>
              {prompts.map((p) => {
                const route = agentIdToRouteSegment(p.agentId);
                return (
                  <tr key={p.agentId}>
                    <td>
                      {p.editable ? (
                        <Link to={`/admin/prompts/${route}/edit`}>{p.name}</Link>
                      ) : (
                        <Link
                          to={`/admin/prompts/${route}/edit`}
                          title="View read-only — platform baselines are edited via PR"
                        >
                          {p.name}
                        </Link>
                      )}
                    </td>
                    <td>
                      <SourceBadge source={p.source} />
                    </td>
                    <td className="admin-table__mono">{p.filename}</td>
                    <td>{formatBytes(p.size)}</td>
                    <td>{formatDate(p.lastModified)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
